/**
 * Owns the app's single Mixpanel SDK instance.
 *
 * Why this module exists: the official `@analytics/mixpanel` plugin loads its
 * own copy of the SDK from `cdn.mxpnl.com` and skips that load only when
 * `window.mixpanel` is already defined. The npm `mixpanel-browser` package
 * never sets that global — it assigns it exclusively under `INIT_SNIPPET`, and
 * module consumers get `INIT_MODULE`. Left alone that yields TWO independent
 * Mixpanel instances on the page (an unpinned CDN copy sending events, our
 * pinned copy recording replay) with split `distinct_id`/`$device_id` and no
 * link between a recording and its events.
 *
 * So we own the load: init the pinned SDK once, publish it as
 * `window.mixpanel`, and hand the vendor plugin a no-op `initialize`. The
 * vendor's `track`/`identify`/`people.set`/`reset` then drive our instance,
 * which means one SDK, one identity, and replay properties attached to events
 * natively by Mixpanel itself.
 */
import { installMixpanelProxyAuth } from './mixpanelProxyAuth';

/** The slice of the Mixpanel browser SDK this app depends on. */
export interface MixpanelSdk {
  init: (token: string, config: Record<string, unknown>) => void;
  track: (event: string, properties?: Record<string, unknown>) => void;
  identify: (distinctId: string) => void;
  reset: () => void;
  people: { set: (props: Record<string, unknown>) => void };
}

declare global {
  interface Window {
    mixpanel?: MixpanelSdk;
  }
}

/**
 * Conservative default: if an env sets a token but forgets the percent, fail to
 * low-volume sampling rather than recording every session. An explicit `0` is
 * honoured as "record nothing".
 */
const DEFAULT_RECORD_SESSIONS_PERCENT = 10;

export interface MixpanelSdkConfig {
  token: string;
  recordSessionsPercent: number;
  trackApiHost: string;
}

export function readMixpanelSdkConfig(): MixpanelSdkConfig {
  const token = (import.meta.env.VITE_MIXPANEL_TOKEN ?? '') as string;
  const raw = import.meta.env.VITE_MIXPANEL_RECORD_SESSIONS_PERCENT as string | undefined;
  const parsed = raw != null && raw !== '' ? Number(raw) : NaN;
  return {
    token,
    recordSessionsPercent: Number.isFinite(parsed) ? parsed : DEFAULT_RECORD_SESSIONS_PERCENT,
    trackApiHost: (import.meta.env.VITE_MIXPANEL_TRACK_API_HOST ?? '') as string,
  };
}

/**
 * The SDK's own defaults, restated because `mixpanel.init` shallow-merges
 * config: passing a partial `api_routes` REPLACES the whole map, leaving every
 * unlisted endpoint `undefined` and its URL as `<host>/undefined`. So any
 * override must ship the complete set.
 */
const DEFAULT_API_ROUTES: Readonly<Record<string, string>> = {
  track: 'track/',
  engage: 'engage/',
  groups: 'groups/',
  record: 'record/',
  flags: 'flags/',
  settings: 'settings/',
};

/**
 * Route map for the proxied setup. Our collector serves `/track` — no trailing
 * slash, unlike Mixpanel's `track/` — so the default would 404 against it.
 * Every other route keeps its Mixpanel-shaped value.
 */
function proxiedApiRoutes(): Record<string, string> {
  return { ...DEFAULT_API_ROUTES, track: 'track' };
}

/**
 * Per-endpoint host overrides that send ONLY event tracking through our own
 * domain. Everything else — profiles (`engage`/`people`), groups, Session
 * Replay (`record`), flags, settings — is left alone and goes straight to
 * Mixpanel, because `get_api_host(type)` falls back to the default `api_host`
 * for any type absent from this map.
 *
 * Both keys are required: the SDK looks the host up under a different endpoint
 * type depending on whether batching is on — `track` on the batched path
 * (`get_batcher_configs().events.api_name`, which is what we run with) and
 * `events` on the unbatched one. Setting one alone silently leaks traffic
 * straight to Mixpanel the moment the other path is taken.
 */
function trackOnlyApiHosts(host: string): Record<string, string> {
  return { track: host, events: host };
}

/**
 * Stand-in published when the real SDK cannot load or init. The vendor plugin
 * reports readiness as `!!window.mixpanel`, so without a global the core would
 * queue every event forever and `ready` would never resolve. Dropping events is
 * the honest failure mode — an unbounded queue is not.
 */
function createNoopSdk(): MixpanelSdk {
  return {
    init: () => undefined,
    track: () => undefined,
    identify: () => undefined,
    reset: () => undefined,
    people: { set: () => undefined },
  };
}

async function loadAndInit(): Promise<MixpanelSdk | null> {
  const { token, recordSessionsPercent, trackApiHost } = readMixpanelSdkConfig();
  // Defensive — `instance.ts` only registers the plugin when a token is set.
  if (!token) return null;

  // The collector authenticates with `x-write-key`, and the key it expects is
  // the Mixpanel project token we already ship — so there is no extra secret to
  // configure. Scoped to the proxy origin: Mixpanel's own endpoints reject
  // unlisted headers at preflight, which would block profiles and replay.
  // Installed before init() because the recorder starts sending during it.
  if (trackApiHost) installMixpanelProxyAuth(trackApiHost, token);

  try {
    const mod = await import('mixpanel-browser');
    const mp = (mod as unknown as { default: MixpanelSdk }).default;
    mp.init(token, {
      // Optional first-party proxy for event tracking only, to keep ingest on
      // our own domain past ad blockers. HOST ONLY — the SDK appends its own
      // route segment, so a full URL here breaks the endpoint. Omitted when
      // unset so every endpoint keeps its Mixpanel default.
      ...(trackApiHost ? { api_hosts: trackOnlyApiHosts(trackApiHost), api_routes: proxiedApiRoutes() } : {}),
      record_sessions_percent: recordSessionsPercent,
      record_heatmap_data: true,
      record_mask_all_text: true,
      record_mask_all_inputs: true,
      autotrack: false,
      autocapture: false,
      // We fire `page_viewed` ourselves from the router (trackPageView.ts).
      track_pageview: false,
      batch_requests: true,
    });
    if (typeof window !== 'undefined') window.mixpanel = mp;
    return mp;
  } catch {
    const stub = createNoopSdk();
    if (typeof window !== 'undefined') window.mixpanel = stub;
    return stub;
  }
}

let bootstrap: Promise<MixpanelSdk | null> | null = null;

/**
 * Load + initialize the SDK exactly once per page. Safe to call concurrently
 * and repeatedly — every caller awaits the same promise.
 */
export function bootstrapMixpanelSdk(): Promise<MixpanelSdk | null> {
  bootstrap ??= loadAndInit();
  return bootstrap;
}

/** Test-only escape hatch. Internal to the analytics surface. */
export function __resetMixpanelSdkForTests(): void {
  bootstrap = null;
  if (typeof window !== 'undefined') delete window.mixpanel;
}
